import type { DaysLayout, InstanceCalendarSourceView, ResolvedItem } from "@keydials/api-client";

/** KEY-820: a day's own segment key — `day-2026-10-12`, or one of its times of day, `day-2026-10-12.morning`. */
export function isDaySegmentKey(key: string): boolean {
  return /^day-\d{4}-\d{2}-\d{2}(\.|$)/.test(key);
}

/** KEY-820: when the time of day a segment names starts ("08:00"), or null for a whole day or one with no time. */
export function slotStartOf(segmentKey: string, layout: DaysLayout | null | undefined): string | null {
  const dot = segmentKey.indexOf(".");
  if (dot < 0) return null;
  const slot = segmentKey.slice(dot + 1);
  return layout?.slots.find((s) => s.key === slot)?.startsAt?.slice(0, 5) ?? null;
}

/** KEY-820: my calendars on this dial that take appointments — the ones "Appointment in your calendar" can use. */
export function appointmentSources(sources: readonly InstanceCalendarSourceView[]): InstanceCalendarSourceView[] {
  return sources.filter((s) => s.isMine && s.connected && s.canAddAppointments === true);
}

/** KEY-820: what a calendar is called in the dialog: its account, and the calendar in it when that isn't the main one. */
export function sourceLabel(source: InstanceCalendarSourceView): string {
  const account = source.accountLabel ?? "Your calendar";
  return source.writeCalendarName ? `${account} — ${source.writeCalendarName}` : account;
}

/** KEY-820: the lengths offered, in minutes; 0 is all day. */
export const APPOINTMENT_LENGTHS: { minutes: number; label: string }[] = [
  { minutes: 15, label: "15 minutes" },
  { minutes: 30, label: "30 minutes" },
  { minutes: 60, label: "1 hour" },
  { minutes: 90, label: "1½ hours" },
  { minutes: 120, label: "2 hours" },
  { minutes: 180, label: "3 hours" },
  { minutes: 0, label: "All day" },
];

/** KEY-821: an appointment's title from its card name, "09:15 · Dentist" → "Dentist". */
export function appointmentTitle(name: string): string {
  const at = name.indexOf(" · ");
  return at >= 0 ? name.slice(at + 3) : name;
}

/** KEY-821: what the change form starts from, for one of my own event items; null for anything else. Times are shown
 * in the dial's zone, as the dial itself shows them. */
export function appointmentBeingChanged(item: ResolvedItem, timeZoneId: string | null | undefined) {
  const cal = item.calendar;
  if (!cal?.canEdit) return null;
  const start = new Date(cal.startUtc);
  const minutes = cal.allDay ? 0 : Math.max(5, Math.round((new Date(cal.endUtc).getTime() - start.getTime()) / 60000));
  const local = cal.allDay
    ? null
    : new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: timeZoneId || "UTC" }).format(start);
  return { itemKey: item.key, title: appointmentTitle(item.name), start: local, minutes, location: cal.location ?? null };
}
